<template>
  <section
    ref="heroRef"
    class="program-hero"
  >
    <div class="program-hero__inner">
      <AppImageFocal
        v-if="props.image"
        :data="props.image"
        :eager="true"
        :renditions="renditions"
        class="program-hero__image"
        :picture-classes="'image-focal__picture--full-height'"
      />
      <LazyAppStreamingVideo
        v-if="props.hls && props.mp4 && state.videoIsPlaying"
        :hls="props.hls"
        :mp4="props.mp4"
        :loop="false"
        :interactive="false"
        :auto-play="true"
        :muted="true"
        :content-fit="'cover'"
        class="program-hero__streaming-video"
        @video-ended="hideVideo"
      />
      <div class="program-hero__heading">
        <div class="program-hero__content">
          <AppHeadingResizing
            :key="headingKey"
            :label="props.title"
            :animate="true"
            :indented="true"
            class="program-hero__heading-resizing"
          />
          <p class="program-hero__text">
            {{ props.introduction }}
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import { useWindowScroll } from '~/composables/useWindowScroll'
import { throttle } from '~/utils/throttle-debounce'

const heroRef = ref<HTMLElement>()

const props = defineProps<{
  title: String,
  introduction: String,
  image: unknown,
  hls?: String,
  mp4?: String,
}>()

const state = reactive({
  scroll: 0,
  videoIsPlaying: true
})

const renditions = [
  { width: 575, height: 944, breakpoint: 'xs' },
  { width: 767, height: 419, breakpoint: 'sm' },
  { width: 991, height: 934, breakpoint: 'md' },
  { width: 1199, height: 654, breakpoint: 'lg' },
  { width: 1920, height: 1048, breakpoint: 'xl' }
]

onMounted(() => {
  // watch for scroll
  const scrollState = useWindowScroll()
  watch(scrollState, (newState) => {
    state.scroll = newState.scroll
  })
})

const headingKey = ref(0)

function rerenderHeading () {
  throttle(headingKey.value += 1, 1000)
}

function hideVideo () {
  state.videoIsPlaying = false
}

onMounted(() => {
  window.addEventListener('resize', rerenderHeading)
})

onUnmounted(() => {
  window.removeEventListener('resize', rerenderHeading)
})

</script>

<style scoped lang="postcss">
.program-hero {
  position: sticky;
  top: 0px;
  overflow: clip;
  @mixin app-max-header-height;

  &__inner {
    position: relative;
    height: 100%;
  }

  &__image {
    position: absolute;
    left: 0px;
    top: 0px;
    width: 100%;
    @mixin app-max-header-height;
  }

  &__streaming-video {
    position: absolute;
    left: 0px;
    top: 0px;
    height: 100%;
    width: 100%;
  }

  &__heading {
    position: absolute;
    bottom: -1px;
    display: flex;
    height: 100%;
    width: 100%;
    @mixin app-max-header-height;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0) 31.68%), linear-gradient(4.33deg, rgba(0, 0, 0, 0.8) 23.82%, rgba(0, 0, 0, 0) 72.31%);
  }

  &__content {
    @mixin container;
    margin-left: 0.75rem;
    margin-right: 0.75rem;
    margin-bottom: 2.5rem;
    margin-top: auto;
    @mixin app-grid;

    @media (--lg) {
      margin-left: auto;
      margin-right: auto;
    }
  }

  &__heading-resizing {
    grid-column: 1 / -1;
    @mixin heading-h1-xl;
  }

  &__text {
    margin-top: 1.5rem;
    @mixin app-intro;
    @mixin app-layout-container-column;
  }

  @supports (height:90cqh) {
    height: 90cqh;
  }

  @supports (height:90svh) {
    height: 90svh;
  }
}
</style>
